import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight modal for user-controlled email preferences.
 * Currently exposes a single toggle for the monthly digest opt-out,
 * backed by POST /api/digest/preferences.
 *
 * The server treats `digest_opt_out=true` as "user does NOT want digests".
 * To keep the UI positive we show a "Monthly digest emails" switch that
 * is ON when digests are allowed, and flip the sense on save.
 */
const EmailPreferencesModal = ({ open, onClose }) => {
  const { user, checkAuth } = useAuth();
  // digestOn = true means "send me digests" (i.e., !digest_opt_out)
  const [digestOn, setDigestOn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && user) {
      setDigestOn(!user.digest_opt_out);
      setJustSaved(false);
      setError('');
    }
  }, [open, user]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const resp = await fetch(`${API}/digest/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ opt_out: !digestOn }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save preferences');
      }
      setJustSaved(true);
      // Refresh user context so the toggle reflects the new server state
      checkAuth();
      setTimeout(() => setJustSaved(false), 1800);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-slate-950 border-white/10 text-white max-w-md"
        data-testid="email-prefs-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600/30 to-cyan-600/30 border border-purple-500/30 flex items-center justify-center">
              <Mail className="w-4 h-4 text-purple-300" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">Email preferences</DialogTitle>
              <DialogDescription className="text-white/40 text-xs">
                Choose what we send you. Transactional emails (verification, receipts) cannot be turned off.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-white text-sm font-medium mb-1">Monthly digest</div>
              <div className="text-white/40 text-xs leading-relaxed">
                Your session stats, new science-backed protocols, and a recap of your brainwave journey — once a month, never spammy.
              </div>
            </div>
            <Switch
              checked={digestOn}
              onCheckedChange={setDigestOn}
              disabled={saving}
              data-testid="digest-toggle"
              className="data-[state=checked]:bg-purple-600"
            />
          </div>

          {error && (
            <p className="text-red-400 text-xs" data-testid="email-prefs-error">{error}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="email-prefs-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white min-w-[110px]"
              data-testid="email-prefs-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : justSaved ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Saved
                </span>
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreferencesModal;
